import React from 'react'
import { Avatar, AvatarImage, AvatarFallback } from '../../components/avatar'
import { cn } from '../../lib/utils'

export interface UIAvatarProps extends React.ComponentProps<typeof Avatar> {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  variant?: 'default' | 'rounded' | 'square'
}

export interface UIAvatarImageProps extends React.ComponentProps<typeof AvatarImage> {
  variant?: 'default' | 'cover' | 'contain'
}

export interface UIAvatarFallbackProps extends React.ComponentProps<typeof AvatarFallback> {
  variant?: 'default' | 'initials' | 'icon' | 'user' | 'assistant'
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
}

/**
 * Enhanced Avatar component following design system best practices
 * Provides consistent avatar styling and behavior with size and variant options
 */
export const UIAvatar = React.forwardRef<
  React.ComponentRef<typeof Avatar>,
  UIAvatarProps
>(({ className, size = 'md', variant = 'default', ...props }, ref) => {
  return (
    <Avatar
      ref={ref}
      className={cn(
        // Size styles
        {
          'h-6 w-6': size === 'xs',
          'h-8 w-8': size === 'sm',
          'h-10 w-10': size === 'md',
          'h-12 w-12': size === 'lg',
          'h-16 w-16': size === 'xl',
        },

        // Variant styles
        {
          'rounded-full': variant === 'default',
          'rounded-lg': variant === 'rounded',
          'rounded-none': variant === 'square',
        },

        className
      )}
      {...props}
    />
  )
})

export const UIAvatarImage = React.forwardRef<
  React.ComponentRef<typeof AvatarImage>,
  UIAvatarImageProps
>(({ className, variant = 'default', ...props }, ref) => {
  return (
    <AvatarImage
      ref={ref}
      className={cn(
        // Variant styles
        {
          'object-cover': variant === 'default' || variant === 'cover',
          'object-contain': variant === 'contain',
        },

        className
      )}
      {...props}
    />
  )
})

export const UIAvatarFallback = React.forwardRef<
  React.ComponentRef<typeof AvatarFallback>,
  UIAvatarFallbackProps
>(({ className, variant = 'default', size = 'md', ...props }, ref) => {
  return (
    <AvatarFallback
      ref={ref}
      className={cn(
        // Base styles - aspect-square for perfect circle (rounded-full is inherited from base AvatarFallback)
        'aspect-square',

        // Size-based text styles
        {
          'text-[10px] font-medium': size === 'xs',
          'text-xs font-medium': size === 'sm',
          'text-sm font-medium': size === 'md',
          'text-base font-medium': size === 'lg',
          'text-lg font-medium': size === 'xl',
        },

        // Variant styles with improved color contrast
        {
          'bg-muted text-foreground': variant === 'default',
          'bg-primary/20 text-primary font-semibold': variant === 'initials',
          'bg-accent text-white': variant === 'icon',
          'bg-primary text-white font-semibold': variant === 'user',
          'bg-secondary text-foreground font-medium': variant === 'assistant',
        },

        className
      )}
      {...props}
    />
  )
})

UIAvatar.displayName = "UIAvatar"
UIAvatarImage.displayName = "UIAvatarImage"
UIAvatarFallback.displayName = "UIAvatarFallback"